import React, { ChangeEvent, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { ChartData } from 'chart.js';
import { LoadingSection } from 'components';
import { GPCDateFilter, gpcMetrics, LineBarChart, MetricDropdown, METRICS_OPTIONS } from 'dashboard409a/components';
import { compactFormatterNumber, RATES_OF_CHANGE_OPTIONS } from 'dashboard409a/utilities';
import { DataPoint, DataSetPoint, DataSets, IRatesOfChangeProps, TimeInterval, timeIntervals } from './types';
import { Logger, shortDate } from '../../../../../utilities';
import { HistoricalData, TimeWindows } from '../../../../services/types';

const RatesOfChange = ({ chartData, isLoading, metricOptions = METRICS_OPTIONS }: IRatesOfChangeProps) => {
  const [currentValue, setCurrentValue] = useState<string>(metricOptions[0]);
  const [dateValue, setDateValue] = useState<TimeInterval>(TimeInterval.MONTH_3);
  const [dataSets, setDataSets] = useState<DataSets>({ datasets: [] });
  const chartDataLength = useMemo(() => chartData.length, [chartData]);

  const handleChange = (event: ChangeEvent<{ value: unknown }>) => {
    setCurrentValue(event.target.value as string);
  };

  const handleDateChange = (event: MouseEvent<{ value: unknown }>) => {
    setDateValue(event.currentTarget.value as TimeInterval);
  };

  // Check if the metric has a % symbol (percentage metric)
  const isPercentageMetric = useMemo(() => 
    gpcMetrics[currentValue]?.symbol === '%'
  , [currentValue]);

  function formatYAxisValue(value: number | string) {
    if (typeof value === 'number') {
      if (isPercentageMetric) {
        return `${Number(value).toFixed(2)}%`;
      }
      return `${compactFormatterNumber.format(value)}${gpcMetrics[currentValue]?.symbol || ''}`;
    }
    return value;
  }

  const options = {
    ...RATES_OF_CHANGE_OPTIONS,
    scales: {
      y: {
        type: 'linear' as const,
        stacked: false,
        grid: { display: true },
        ticks: {
          callback: formatYAxisValue,
          autoSkip: true,
          maxTicksLimit: 9,
        },
      },
    },
  };

  const generateDateTickerMapping = useCallback((allDataForMonthInterval: DataPoint[]) => {
    const dateTickerMap = new Map<string, string[]>();

    allDataForMonthInterval.forEach((i: DataPoint) => {
      const hasDate = dateTickerMap.has(i.dt);
      const ticker = i.ticker_symbol as string;

      if (hasDate) {
        const curValues = dateTickerMap.get(i.dt)!;
        dateTickerMap.set(i.dt, [...curValues, ticker]);
      } else {
        dateTickerMap.set(i.dt, [ticker]);
      }
    });

    return dateTickerMap;
  }, []);

  const getDatesFromMappingWithMatchingCount = useCallback(
    (dateMap: Map<string, string[]>): string[] => {
      const datesWithMatchingCount: string[] = [];
      dateMap.forEach((val: string[], key: string) => {
        if (val.length === chartDataLength) {
          datesWithMatchingCount.push(key);
        } else {
          Logger.debug(`The tickers: ${val.join(', ')} have a data mismatch on the date: ${key}`);
        }
      });

      return datesWithMatchingCount;
    },
    [chartDataLength]
  );

  const getAllDateForInterval = useCallback(
    (interval: TimeInterval): DataPoint[] => {
      const allDataForInterval = chartData
        .map((i: HistoricalData) => i[interval].map(comp => ({ ...comp, ticker_symbol: i.ticker_symbol })))
        .flat();

      return allDataForInterval;
    },
    [chartData]
  );

  const filterDatesWithCompleteData = useCallback((interval: TimeInterval, datesWithCompleteData: string[]) => {
    // remove any dates from the interval that do not have a matching count with all other dates
    chartData.forEach((i: HistoricalData) => {
      // eslint-disable-next-line no-param-reassign
      i[interval] = i[interval].filter((currItem: TimeWindows) => datesWithCompleteData.includes(currItem.dt));
    });
  }, [chartData]);

  const createDataSets = useCallback((dateValueParam: TimeInterval): DataSets => ({
    datasets: chartData.map((item: HistoricalData) => ({
      type: 'line',
      label: item.ticker_symbol,
      data: item[dateValueParam].map(
        (info: TimeWindows): DataSetPoint => ({
          x: shortDate(info.dt) ?? '',
          y: Number(info[gpcMetrics[currentValue].default as keyof TimeWindows]),
        })
      ),
      backgroundColor: item.color ?? '',
      borderColor: item.color ?? '',
      pointRadius: 0,
    })),
  }), [chartData, currentValue]);

  const getAverageData = useCallback((dataSetsParam: DataSets) => {
    // Find dataset with maximum length to use as reference
    let maxLength = 0;
    let maxLengthIndex = 0;
    
    dataSetsParam.datasets.forEach((ds, i) => {
      const currentLength = ds.data?.length || 0;
      if (currentLength > maxLength) {
        maxLength = currentLength;
        maxLengthIndex = i;
      }
    })
    
    const referenceDataset = dataSetsParam.datasets[maxLengthIndex];
    
    // Use the reference dataset to calculate averages
    const averageData = referenceDataset?.data?.map((point, index) => {
      const valuesAtIndex = dataSetsParam?.datasets?.map(ds => ds.data?.[index]?.y).filter(Boolean);
      const avgAtIndex = valuesAtIndex.reduce((sum, val) => sum + val, 0) / valuesAtIndex.length;
      return {
        x: point.x,
        y: avgAtIndex,
      };
    });
    
    return averageData;
  }, []);

  /**
   *
   * The api may return a different number of tickers for each interval,
   * we only want to display the dates that have a matching count with all other dates
   *
   */
  const processChartData = useCallback(() => {
    timeIntervals.forEach((interval: TimeInterval) => {
      const timeSeriesDataForInterval = getAllDateForInterval(interval);
      const dateTickerMap = generateDateTickerMapping(timeSeriesDataForInterval);
      const datesWithCompleteData = getDatesFromMappingWithMatchingCount(dateTickerMap);

      filterDatesWithCompleteData(interval, datesWithCompleteData);
    });

    const tempDataSets = createDataSets(dateValue);
    if (tempDataSets.datasets.length > 0) {
      const averageDataset = {
        type: 'line',
        label: 'Average',
        data: getAverageData(tempDataSets),
        borderColor: 'red',
        borderWidth: 2,
        pointRadius: 0,
        fill: false,
        borderDash: [5, 5],
      };
      const dataSetsWithAverage = {
        ...tempDataSets,
        datasets: [...tempDataSets.datasets, averageDataset],
      };
      setDataSets(dataSetsWithAverage as unknown as DataSets);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateValue, generateDateTickerMapping, getAllDateForInterval, getDatesFromMappingWithMatchingCount, createDataSets, filterDatesWithCompleteData]);

  useEffect(() => {
    processChartData();
  }, [processChartData]);

  if (!dataSets.datasets.length) {
    return <></>;
  }

  return isLoading || !chartData ? (
    <LoadingSection />
  ) : (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: 1,
          m: 1,
        }}>
        <MetricDropdown value={currentValue} title="Metric" handleChange={handleChange} options={metricOptions} />
        <GPCDateFilter value={dateValue} handleChange={handleDateChange} />
      </Box>
      <LineBarChart data={dataSets as unknown as ChartData} options={options} />
    </>
  );
};

export default RatesOfChange;
